/**
 * 页面关闭/刷新警告 Hook
 * 当有正在进行的上传时，提醒用户关闭页面会中断上传
 */

import { useEffect, useMemo } from 'react'
import { useUploadStore } from '@/stores/upload'

/**
 * 页面关闭警告 Hook
 * @param enabled 是否启用警告，默认为 true
 */
export function useBeforeUnload(enabled = true): void {
  // 订阅 sessions 状态，计算是否有活动上传
  const sessions = useUploadStore((state) => state.sessions)

  // 使用 useMemo 计算是否有活动上传，避免每次渲染都重新计算
  const hasActiveUploads = useMemo(() => {
    return Object.values(sessions).some((session) => session.status === 'uploading')
  }, [sessions])

  useEffect(() => {
    if (!enabled || !hasActiveUploads) {
      return undefined
    }

    /**
     * 处理页面关闭事件
     * @param event BeforeUnloadEvent
     * @returns 提示消息
     */
    function handleBeforeUnload(event: BeforeUnloadEvent): string {
      const message = '您有图片正在上传，关闭页面将中断上传。确定要离开吗？'
      event.preventDefault()
      return message
    }

    const previousHandler = window.onbeforeunload
    window.onbeforeunload = handleBeforeUnload

    return (): void => {
      window.onbeforeunload = previousHandler
    }
  }, [enabled, hasActiveUploads])
}
